// src/LearnerContext.js
// Học sinh đang mở khoá trên máy này, popup nhập SĐT phụ huynh + tên, và thanh "Học viên: …" dưới menu.
// Trang nào gặp nội dung bị khoá thì gọi openGate(onUnlocked).
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Lock, X, GraduationCap, LogOut } from "lucide-react";
import {
  getLearner,
  loginLearner,
  logoutLearner,
  refreshLearner,
  LEARNER_EVENT,
} from "./learnerApi";

const LearnerContext = createContext({
  learner: null,
  openGate: () => {},
  logout: () => {},
});

export const useLearner = () => useContext(LearnerContext);

const UnlockModal = ({ onClose, onUnlocked }) => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await loginLearner(phone, name);
    setBusy(false);
    if (result.ok) {
      toast.success(`Chào ${result.student.fullName}! Chúc em học tốt.`);
      onUnlocked();
      return;
    }
    setError(
      result.attemptsLeft > 0
        ? `${result.message} (còn ${result.attemptsLeft} lần thử)`
        : result.message
    );
  };

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-indigo-400";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 md:p-6 relative"
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1 text-gray-500" aria-label="Đóng">
          <X size={22} />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Lock size={22} className="text-indigo-600" />
          <h3 className="text-lg font-bold text-gray-800">Dành cho học viên Mây Xanh</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Nội dung học dành cho học sinh đang theo học tại trung tâm. Nhập số điện thoại phụ huynh (đã đăng
          ký với trung tâm) và tên của em để mở khoá. Chỉ cần nhập một lần trên mỗi máy.
        </p>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Số điện thoại phụ huynh</label>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Ví dụ: 0901 234 567"
          className={`${inputClass} mb-3`}
          required
        />
        <label className="block text-sm font-semibold text-gray-700 mb-1">Tên của học sinh</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ví dụ: Thiên An hoặc Nguyễn Thiên An"
          className={`${inputClass} mb-1`}
          required
        />
        <p className="text-xs text-gray-500 mb-3">Gõ tên hoặc cả họ tên đều được, có dấu hay không dấu đều được.</p>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2 mb-3">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5"
        >
          {busy ? "Đang kiểm tra..." : "Mở khoá"}
        </button>
        <p className="text-xs text-gray-500 mt-3 text-center">
          Chưa vào được? Liên hệ trung tâm để kiểm tra số điện thoại đã đăng ký.
        </p>
      </form>
    </div>
  );
};

// Thanh nhỏ dưới menu: đang học với tư cách học sinh nào, nút thoát (máy dùng chung / đổi anh chị em)
export const LearnerBar = () => {
  const { learner, logout } = useLearner();
  if (!learner) return null;
  return (
    <div className="bg-indigo-50 border-b border-indigo-100 text-sm text-indigo-900">
      <div className="container mx-auto px-4 py-1.5 flex items-center justify-between gap-2">
        <span className="flex items-center min-w-0">
          <GraduationCap size={16} className="mr-1.5 flex-shrink-0" />
          <span className="truncate">
            Học viên: <b>{learner.fullName}</b>
            {learner.classes?.length > 0 && ` · ${learner.classes.join(", ")}`}
          </span>
        </span>
        <button
          type="button"
          onClick={logout}
          className="flex items-center text-indigo-700 font-semibold flex-shrink-0"
          title="Thoát (để học sinh khác dùng máy này)"
        >
          <LogOut size={14} className="mr-1" /> Thoát
        </button>
      </div>
    </div>
  );
};

export const LearnerProvider = ({ children }) => {
  const [learner, setLearnerState] = useState(() => getLearner()?.student || null);
  const [gateOpen, setGateOpen] = useState(false);
  const onUnlockedRef = useRef(null);

  // Đồng bộ khi mở khoá / thoát (kể cả ở tab khác) và kiểm tra lại phiên khi mở trang
  useEffect(() => {
    const sync = () => setLearnerState(getLearner()?.student || null);
    window.addEventListener(LEARNER_EVENT, sync);
    window.addEventListener("storage", sync);
    refreshLearner();
    return () => {
      window.removeEventListener(LEARNER_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const openGate = useCallback((onUnlocked) => {
    onUnlockedRef.current = onUnlocked || null;
    setGateOpen(true);
  }, []);

  const logout = useCallback(async () => {
    if (!window.confirm("Thoát khỏi máy này? Lần sau cần nhập lại số điện thoại phụ huynh.")) return;
    await logoutLearner();
    toast.success("Đã thoát.");
  }, []);

  return (
    <LearnerContext.Provider value={{ learner, openGate, logout }}>
      {children}
      {gateOpen && (
        <UnlockModal
          onClose={() => setGateOpen(false)}
          onUnlocked={() => {
            setGateOpen(false);
            onUnlockedRef.current?.();
          }}
        />
      )}
    </LearnerContext.Provider>
  );
};
